/**
 * FP3-032D — Calm in-app confirmation dialog.
 *
 * Replaces native `window.confirm(...)` across the app. Wraps shadcn
 * AlertDialog so the destructive/neutral styling stays consistent and
 * every callsite gets the same calm look + a11y semantics.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={pending}
 *     onOpenChange={setPending}
 *     title="Delete document?"
 *     description="This will remove the document from the student record."
 *     confirmLabel="Delete document"
 *     destructive
 *     onConfirm={handleDelete}
 *     testId="documents-delete"
 *   />
 *
 * Notes:
 * - `destructive` swaps the confirm-button tone to the calm-red used
 *   elsewhere in the app for delete actions.
 * - The action button stays enabled until the caller's `onConfirm`
 *   resolves; while pending, the button shows a "…" suffix so users
 *   know the click registered.
 * - We intentionally do not auto-close on confirm — the caller closes
 *   via `onOpenChange(false)` so it can wait for the API call.
 */
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  testId,
}) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (busy) return;
        onOpenChange?.(o);
      }}
    >
      <AlertDialogContent
        data-testid={testId ? `${testId}-dialog` : undefined}
        className="rounded-2xl border-[#E5E3DB]"
      >
        <AlertDialogHeader>
          <AlertDialogTitle
            data-testid={testId ? `${testId}-title` : undefined}
            className="text-[#1F2937] heading-font"
          >
            {title}
          </AlertDialogTitle>
          {description && (
            <AlertDialogDescription
              data-testid={testId ? `${testId}-description` : undefined}
              className="text-[#6B7280]"
            >
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid={testId ? `${testId}-cancel` : undefined}
            disabled={busy}
            className="rounded-lg border-[#E5E3DB] text-[#6B7280]"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={testId ? `${testId}-confirm` : undefined}
            onClick={(e) => {
              // Stop AlertDialog from auto-closing — the caller closes
              // via onOpenChange once its async work resolves so the
              // user sees a busy state instead of an instant flash.
              e.preventDefault();
              handleConfirm();
            }}
            className={
              destructive
                ? "rounded-lg bg-[#B85C5C] hover:bg-[#a04e4e] text-white"
                : "rounded-lg bg-[#1E3F33] hover:bg-[#173127] text-white"
            }
          >
            {busy ? `${confirmLabel}…` : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
